import React, { useEffect, useState, useMemo } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { TripMetadata } from '../types';
import { useTripMetadata } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';

// TripCard component for displaying individual trip cards
const TripCard: React.FC<{
  trip: TripMetadata;
  index: number;
  navigate: (path: string) => void;
}> = ({ trip, index, navigate }) => {
  return (
    <div 
      className="card group cursor-pointer animate-slide-up"
      style={{ animationDelay: `${index * 0.1}s` }}
      onClick={() => navigate(`/trips/${trip.tripId}`)}
      role="button"
      tabIndex={0}
      aria-label={`View trip ${trip.destination}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`/trips/${trip.tripId}`);
        }
      }}
    >
      <div className="p-6">
        {/* Trip Cover Image */}
        <div className="w-full h-48 rounded-xl mb-4 overflow-hidden group-hover:scale-105 transition-transform duration-300">
          {trip.coverImage ? (
            <img 
              src={trip.coverImage} 
              alt={`${trip.destination} cover`}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to placeholder if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  parent.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center"><span class="text-6xl">🗺️</span></div>';
                }
              }}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
              <span className="text-6xl">🗺️</span>
            </div>
          )}
        </div>
        
        {/* Trip Details */}
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-airbnb-800 group-hover:text-primary-500 transition-colors duration-200">
            {trip.destination}
          </h3>
          
          <div className="flex items-center text-airbnb-600">
            <span className="text-sm font-medium">
              {trip.startDate} - {trip.endDate}
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="badge badge-primary">
                {trip.daysCount} days
              </span>
              <span className="badge badge-secondary">
                {trip.destination}
              </span>
            </div>
          </div>
          
          {trip.updatedAt && (
            <div className="text-sm text-airbnb-600">
              Updated: {new Date(trip.updatedAt).toLocaleDateString()}
            </div>
          )}
        </div>
        
        {/* Action Button */}
        <div className="mt-6">
          <Link 
            to={`/trips/${trip.tripId}`}
            className="btn-primary w-full text-center group-hover:scale-105 transition-transform duration-200"
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );
};

const MyTripsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, getUserId, loading: authLoading } = useAuth();
  const [infoBannerMessage, setInfoBannerMessage] = useState<string | null>(null);

  // Collapsible sections for trip groups
  const [collapsedGroups, setCollapsedGroups] = useState({
    inProgress: false, // open by default
    future: false,     // open by default
    old: true          // closed by default
  });

  // Get user ID from authentication
  const userId = getUserId();
  
  // Debug logging
  console.log('🔍 MyTripsPage - Auth loading:', authLoading);
  console.log('🔍 MyTripsPage - User object:', user);
  console.log('🔍 MyTripsPage - User ID:', userId);
  console.log('🔍 MyTripsPage - Is user authenticated?', !!user);
  
  // Check localStorage for Amplify tokens
  console.log('🔍 LocalStorage keys:', Object.keys(localStorage));
  console.log('🔍 SessionStorage keys:', Object.keys(sessionStorage));

  // Use the new optimized hook for trip metadata
  // Pass userId directly - the hook will handle null values
  console.log('🔍 MyTripsPage - Calling useTripMetadata with userId:', userId);
  const { trips: tripsMetadata, loading, error, refetch } = useTripMetadata(userId);

  // Sort trips into categories based on current date
  const sortedTrips = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    
    const inProgressTrips: typeof tripsMetadata = [];
    const futureTrips: typeof tripsMetadata = [];
    const oldTrips: typeof tripsMetadata = [];
    
    tripsMetadata.forEach(trip => {
      const startDate = new Date(trip.startDate);
      const endDate = new Date(trip.endDate);
      
      // Reset time to start of day for accurate comparison
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      if (today >= startDate && today <= endDate) {
        // Trip is currently in progress
        inProgressTrips.push(trip);
      } else if (today < startDate) {
        // Trip is in the future
        futureTrips.push(trip);
      } else {
        // Trip is in the past
        oldTrips.push(trip);
      }
    });
    
    // Sort future trips by start date (closest first)
    futureTrips.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
    
    // Sort old trips by end date (most recent first)
    oldTrips.sort((a, b) => new Date(b.endDate).getTime() - new Date(a.endDate).getTime());
    
    return {
      inProgress: inProgressTrips,
      future: futureTrips,
      old: oldTrips
    };
  }, [tripsMetadata]);

  // Check if modal should be opened from URL parameter
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      navigate('/create-trip', { replace: true });
    }
  }, [searchParams, navigate]);

  // Show informational banner passed via navigation state (e.g., trip creation still processing)
  useEffect(() => {
    const state = location.state as any;
    const message = typeof state?.infoMessage === 'string' ? state.infoMessage : null;
    if (!message) return;

    setInfoBannerMessage(message);

    // Clear state so banner doesn't reappear on back/refresh loops
    navigate('/trips', { replace: true, state: null });
  }, [location.state, navigate]);

  if (authLoading || loading) {
    return (
      <div className="flex min-h-screen bg-white items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="loading-spinner h-12 w-12 mx-auto mb-6"></div>
          <p className="text-lg text-airbnb-600 font-medium">
            {authLoading ? 'Authenticating...' : 'Loading your trips...'}
          </p>
        </div>
      </div>
    );
  }

  // Show authentication required message if no user
  if (!authLoading && !user) {
    return (
      <div className="flex min-h-screen bg-white items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="text-yellow-500 text-6xl mb-6">🔐</div>
          <h1 className="text-3xl font-bold text-airbnb-800 mb-3">Authentication Required</h1>
          <p className="text-airbnb-600 mb-4 text-lg">Please sign in to view your trips.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen bg-white items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="text-red-500 text-6xl mb-6">⚠️</div>
          <h1 className="text-3xl font-bold text-airbnb-800 mb-3">Error Loading Trips</h1>
          <p className="text-airbnb-600 mb-4 text-lg">{error}</p>
          <div className="text-sm text-airbnb-500 mb-8">
            User ID: {userId || 'undefined'} | Backend running: {error.includes('Failed to fetch') ? '❌ No' : '✅ Yes'}
          </div>
          <div className="space-x-4">
            <button 
              onClick={() => refetch()} 
              className="btn-primary btn-lg"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {infoBannerMessage && (
          <div className="mb-8 rounded-xl border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-semibold text-blue-900">Heads up</div>
                <div className="mt-1 text-sm text-blue-800">{infoBannerMessage}</div>
              </div>
              <button
                type="button"
                className="text-sm font-semibold text-blue-900 hover:underline"
                onClick={() => setInfoBannerMessage(null)}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-12">
          <div className="mb-6 sm:mb-0">
            <h1 className="text-4xl sm:text-5xl font-bold text-airbnb-800 mb-2">
              My Trips
            </h1>
            <p className="text-lg text-airbnb-600">
              Plan and manage your travel adventures
            </p>
          </div>
          <button 
            onClick={() => navigate('/create-trip')} 
            className="btn-primary btn-lg self-start sm:self-auto"
          >
            + Create New Trip
          </button>
        </div>

        {tripsMetadata.length === 0 ? (
          <div className="text-center py-20 animate-fade-in">
            <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <span className="text-4xl">✈️</span>
            </div>
            <h2 className="text-3xl font-bold text-airbnb-800 mb-4">No trips yet!</h2>
            <p className="text-lg text-airbnb-600 mb-10 max-w-md mx-auto">
              Start planning your next adventure by creating a new trip. Let's make some memories!
            </p>
            <button 
              onClick={() => navigate('/create-trip')} 
              className="btn-primary btn-lg"
            >
              Create Your First Trip
            </button>
          </div>
        ) : (
          <div className="space-y-12 animate-fade-in">
            {/* In Progress Trips */}
            {sortedTrips.inProgress.length > 0 && (
              <div>
                <button
                  type="button"
                  className="w-full text-left mb-6 flex items-center justify-between group"
                  aria-expanded={!collapsedGroups.inProgress}
                  onClick={() => setCollapsedGroups(prev => ({ ...prev, inProgress: !prev.inProgress }))}
                >
                  <div className="flex items-center">
                    <span className="w-3 h-3 bg-green-500 rounded-full mr-3"></span>
                    <h2 className="text-2xl font-bold text-airbnb-800">
                      In Progress
                    </h2>
                    <span className="ml-3 text-airbnb-600 text-sm">({sortedTrips.inProgress.length})</span>
                  </div>
                  <span
                    className={`transform transition-transform duration-200 text-airbnb-600 group-hover:text-airbnb-800 ${
                      collapsedGroups.inProgress ? '' : 'rotate-180'
                    }`}
                    aria-hidden="true"
                  >
                    ▾
                  </span>
                </button>
                {!collapsedGroups.inProgress && (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {sortedTrips.inProgress.map((trip, index) => (
                      <TripCard 
                        key={trip.tripId} 
                        trip={trip} 
                        index={index}
                        navigate={navigate}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Future Trips */}
            {sortedTrips.future.length > 0 && (
              <div>
                <button
                  type="button"
                  className="w-full text-left mb-6 flex items-center justify-between group"
                  aria-expanded={!collapsedGroups.future}
                  onClick={() => setCollapsedGroups(prev => ({ ...prev, future: !prev.future }))}
                >
                  <div className="flex items-center">
                    <span className="w-3 h-3 bg-blue-500 rounded-full mr-3"></span>
                    <h2 className="text-2xl font-bold text-airbnb-800">
                      Future Trips
                    </h2>
                    <span className="ml-3 text-airbnb-600 text-sm">({sortedTrips.future.length})</span>
                  </div>
                  <span
                    className={`transform transition-transform duration-200 text-airbnb-600 group-hover:text-airbnb-800 ${
                      collapsedGroups.future ? '' : 'rotate-180'
                    }`}
                    aria-hidden="true"
                  >
                    ▾
                  </span>
                </button>
                {!collapsedGroups.future && (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {sortedTrips.future.map((trip, index) => (
                      <TripCard 
                        key={trip.tripId} 
                        trip={trip} 
                        index={index}
                        navigate={navigate}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Old Trips */}
            {sortedTrips.old.length > 0 && (
              <div>
                <button
                  type="button"
                  className="w-full text-left mb-6 flex items-center justify-between group"
                  aria-expanded={!collapsedGroups.old}
                  onClick={() => setCollapsedGroups(prev => ({ ...prev, old: !prev.old }))}
                >
                  <div className="flex items-center">
                    <span className="w-3 h-3 bg-gray-500 rounded-full mr-3"></span>
                    <h2 className="text-2xl font-bold text-airbnb-800">
                      Past Trips
                    </h2>
                    <span className="ml-3 text-airbnb-600 text-sm">({sortedTrips.old.length})</span>
                  </div>
                  <span
                    className={`transform transition-transform duration-200 text-airbnb-600 group-hover:text-airbnb-800 ${
                      collapsedGroups.old ? '' : 'rotate-180'
                    }`}
                    aria-hidden="true"
                  >
                    ▾
                  </span>
                </button>
                {!collapsedGroups.old && (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {sortedTrips.old.map((trip, index) => (
                      <TripCard 
                        key={trip.tripId} 
                        trip={trip} 
                        index={index}
                        navigate={navigate}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
    </div>
  );
};

export default MyTripsPage;
